import { useState } from 'react';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useUsers, useRoles, useUpdateUser, useDeleteUser } from '../../hooks/useUsers';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Input, Select } from '../common/Input';
import { Skeleton } from '../common/Skeleton';
import { formatDate } from '../../utils/datetime';
import { Role, User } from '../../types';

const statusTone: Record<string, string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  locked: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  inactive: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

export function UserListPage() {
  useDocumentTitle('Users');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [deleting, setDeleting] = useState<User | null>(null);
  const { user: currentUser } = useAuth();

  const filters: Record<string, string> = {};
  if (search) filters.search = search;
  if (statusFilter) filters.status = statusFilter;
  if (roleFilter) filters.role_id = roleFilter;

  const { data: users, isLoading } = useUsers(filters);
  const { data: roles } = useRoles();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();

  function handleRoleChange(userId: number, roleId: number) {
    updateUser.mutate({ id: userId, data: { role_id: roleId } as any });
  }

  function handleStatusToggle(userId: number, currentStatus: string) {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    updateUser.mutate({ id: userId, data: { status: newStatus } as any });
  }

  function handleDelete() {
    if (!deleting) return;
    deleteUser.mutate(deleting.id, { onSettled: () => setDeleting(null) });
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">User management</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Setting a locked account back to Active also clears its failed sign-in count.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <Input
          type="search"
          placeholder="Search by name or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          aria-label="Search users"
          className="flex-1 min-w-[12rem]"
        />
        <Select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
          className="sm:w-auto"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="locked">Locked</option>
        </Select>
        <Select
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}
          aria-label="Filter by role"
          className="sm:w-auto"
        >
          <option value="">All roles</option>
          {roles?.map((r: Role) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </Select>
      </div>

      <Card>
        {isLoading ? (
          <div className="p-4 space-y-3" aria-busy="true">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : !users?.length ? (
          <p className="p-8 text-center text-gray-500 dark:text-gray-400">No users match those filters.</p>
        ) : (
          // overflow-x-auto, not overflow-hidden: the previous wrapper clipped
          // the table on narrow screens and prevented the scroll that saves it.
          <div className="overflow-x-auto">
            <table className="w-full min-w-[40rem]">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr>
                  {['User', 'Role', 'Status', 'Created'].map(h => (
                    <th key={h} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {users.map(u => {
                  const isSelf = u.id === currentUser?.id;
                  return (
                    <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium">{u.name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{u.email}</p>
                      </td>
                      <td className="px-6 py-4">
                        <Select
                          value={u.role_id}
                          onChange={e => handleRoleChange(u.id, parseInt(e.target.value, 10))}
                          disabled={isSelf}
                          aria-label={`Role for ${u.name}`}
                          className="w-auto"
                        >
                          {roles?.map((r: Role) => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </Select>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          type="button"
                          onClick={() => handleStatusToggle(u.id, u.status)}
                          disabled={isSelf}
                          aria-label={`${u.status === 'active' ? 'Deactivate' : 'Activate'} ${u.name}`}
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusTone[u.status] ?? statusTone.inactive} ${
                            isSelf ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:opacity-80'
                          } focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500`}
                        >
                          {u.status}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 tabular-nums">
                        {formatDate(u.created_at)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {!isSelf && (
                          <Button variant="ghost" size="sm" onClick={() => setDeleting(u)}>Delete</Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={!!deleting}
        title="Delete user"
        message={`${deleting?.name} (${deleting?.email}) will be permanently deleted and signed out of every device. This cannot be undone.`}
        confirmLabel="Delete user"
        requireText={deleting?.email}
        loading={deleteUser.isPending}
        onConfirm={handleDelete}
        onClose={() => setDeleting(null)}
      />
    </div>
  );
}
